import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "grid grid-cols-12 gap-6" }
const _hoisted_2 = { class: "p-4 md:p-6 col-span-12 space-y-6" }
const _hoisted_3 = { class: "flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6" }
const _hoisted_4 = { class: "md:pt-8 flex-shrink-0" }
const _hoisted_5 = { class: "flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6" }
const _hoisted_6 = { class: "flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-3" }
const _hoisted_7 = { class: "flex-1 flex flex-wrap flex-col md:flex-row" }
const _hoisted_8 = { class: "p-4 md:p-6 col-span-12 flex flex-wrap lg:space-x-6" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", { class: "border-t lg:border-r flex-shrink-0 w-full lg:w-auto mt-8 mb-7 lg:my-0" }, null, -1)
const _hoisted_10 = { class: "lg:flex-1 grid grid-cols-2 gap-6 w-full" }

import {
  MedKitButton,
  MedKitDateTimeField,
  MedKitRadioGroup,
  MedKitSearch,
  MedKitSelect,
  MedKitSwitch,
  MedKitTextField,
} from '@bigpicturemedical/medkit'
import {
  DvCodedText,
  DvCodedTextFilter,
  DvCodedTextList,
  DvDateTime,
  DvText,
  IPath,
  IStructSdt,
  NodeRepeater,
  TerminologySearch,
  useWebTemplateHelpers,
} from '@bigpicturemedical/openehr-vue'
import { isEqual, omit, cloneDeep } from 'lodash-es'
import { PropType, watch, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { BlockContainer, BlockHeader, BlockPanel, BlockPanelHeader, BlockWell } from './_layout'


export default /*#__PURE__*/_defineComponent({
  __name: 'ProblemListBlock',
  props: {
  path: {
    type: Object as PropType<IPath>,
    required: true
  },
  name: {
    type: String,
    required: true
  },
  description: {
    type: String,
    required: true
  },
  errors: {
    type: Array as PropType<Array<string>>,
    default: () => []
  }
},
  setup(__props) {

const props = __props;



const { getData, updateData, data } = useWebTemplateHelpers()
const { t } = useI18n()

const originalNodes = cloneDeep(getData(props.path.add('problem_diagnosis')) ?? [])
const shouldRunWatcher = ref(true)

watch(data, async (updatedComposition: IStructSdt) => {
  if (!shouldRunWatcher.value) {
    return
  }

  shouldRunWatcher.value = false

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore-next-line
  const problemNodes = updatedComposition?.problem_diagnosis_list?.problem_diagnosis_list?.problem_diagnosis ?? []

  problemNodes.forEach((problemNode, index) => {
    const latestVersionOfProblem = omit(problemNode, 'last_updated')
    const originalVersionOfProblem = omit(originalNodes[index] ?? {}, 'last_updated')

    updateData(
      props.path.add(`problem_diagnosis.${index}.last_updated`),
      !isEqual(latestVersionOfProblem, originalVersionOfProblem)
        ? new Date().toISOString()
        : originalNodes[index].last_updated
    )
  })
  nextTick(() => shouldRunWatcher.value = true)
}, { deep: true })

// This is a hack to set the active diagnosis to active by default. It is needed because EHRBase is not returning the
// default value for active diagnosis. This can be replaced with the default value when the EHRBase issue is resolved.
// Ref; https://github.com/ehrbase/ehrbase/issues/881
const addNewProblem = (addItem) => {
  addItem()

  const problemList: Array<object> = getData(props.path.add('problem_diagnosis'))

  updateData(
    props.path.add(`problem_diagnosis.${problemList.length - 1}.problem_diagnosis_qualifier.active_diagnosis`),
    {
      '|code': '55561003',
      '|value': 'Active',
      '|terminology': 'SNOMED-CT'
    }
  )
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BlockContainer), { class: "space-y-6" }, {
    default: _withCtx(({ display }) => [
      _createVNode(_unref(BlockHeader), {
        heading: __props.name,
        description: __props.description
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(DvCodedText), {
            path: props.path.add('exclusion_-_global.global_exclusion_of_problems_diagnoses')
          }, {
            default: _withCtx(({ value, setValue, items }) => [
              _createVNode(_unref(MedKitSwitch), {
                class: "w-full md:w-auto",
                "model-value": !! value,
                label: _unref(t)('problemList.globalExclusionOfProblemsDiagnoses'),
                "label-position": "start",
                contained: "",
                "onUpdate:modelValue": ($event: any) => (setValue($event ? items[0] : undefined))
              }, null, 8, ["model-value", "label", "onUpdate:modelValue"])
            ]),
            _: 1
          }, 8, ["path"])
        ]),
        _: 1
      }, 8, ["heading", "description"]),
      _createVNode(_unref(NodeRepeater), {
        path: props.path.add('problem_diagnosis'),
        "present-if": ! _unref(getData)(props.path.add('exclusion_-_global.global_exclusion_of_problems_diagnoses')),
        reverse: ""
      }, {
        header: _withCtx(({ addItem }) => [
          _createVNode(_unref(MedKitButton), {
            class: "w-full",
            kind: "repeater",
            label: _unref(t)('problemList.problems.add'),
            icon: "plus",
            "icon-position": "start",
            onClick: ($event: any) => (addNewProblem(addItem))
          }, null, 8, ["label", "onClick"])
        ]),
        default: _withCtx(({ index: problemDiagnosisIndex, remove }) => [
          _createVNode(_unref(BlockPanel), null, {
            default: _withCtx(() => [
              _createVNode(_unref(BlockPanelHeader), {
                title: _unref(t)('problemList.problems.title')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(MedKitButton), {
                    size: "small",
                    label: _unref(t)('problemList.problems.remove'),
                    "icon-position": "only",
                    icon: "delete",
                    onClick: remove
                  }, null, 8, ["label", "onClick"])
                ]),
                _: 2
              }, 1032, ["title"]),
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(_unref(DvCodedText), {
                      path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.problem_diagnosis_name`)
                    }, {
                      default: _withCtx(({ name: fieldName, terminology, value, setValue, errors: fieldErrors, validate, required }) => [
                        _createVNode(_unref(TerminologySearch), { terminology: terminology }, {
                          default: _withCtx(({ search, searching, results, error: terminologySearchError }) => [
                            _createVNode(_unref(MedKitSearch), {
                              class: "flex-1",
                              "model-value": value ? { label: value['|value'], value: value } : undefined,
                              error: terminologySearchError ?? fieldErrors[0],
                              label: fieldName,
                              "is-loading": searching,
                              options: results.map(result => ({
                        label: result['|value'],
                        value: result,
                      })),
                              placeholder: _unref(t)('problemList.problem.problem_diagnosis_name.placeholder'),
                              required: required,
                              "onUpdate:query": search,
                              "onUpdate:modelValue": ($event: any) => (setValue($event ? $event.value : undefined)),
                              onInput: validate
                            }, null, 8, ["model-value", "error", "label", "is-loading", "options", "placeholder", "required", "onUpdate:query", "onUpdate:modelValue", "onInput"])
                          ]),
                          _: 2
                        }, 1032, ["terminology"])
                      ]),
                      _: 2
                    }, 1032, ["path"]),
                    _createVNode(_unref(DvCodedText), {
                      path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.problem_diagnosis_qualifier.active_diagnosis`)
                    }, {
                      default: _withCtx(({ value, setValue, items }) => [
                        _createElementVNode("div", _hoisted_4, [
                          _createVNode(_unref(MedKitSwitch), {
                            "model-value": value['|code'] === '55561003',
                            label: _unref(t)('problemList.problem.active_diagnosis.label'),
                            "label-position": "start",
                            contained: "",
                            "onUpdate:modelValue": ($event: any) => (setValue($event ? items[0] : items[1]))
                          }, null, 8, ["model-value", "label", "onUpdate:modelValue"])
                        ])
                      ]),
                      _: 2
                    }, 1032, ["path"])
                  ]),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_unref(DvCodedText), {
                      path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.diagnostic_certainty`)
                    }, {
                      default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                        _createVNode(_unref(MedKitSelect), {
                          class: "lg:flex-1",
                          error: fieldErrors[0],
                          label: fieldName,
                          "model-value": selected,
                          options: options,
                          placeholder: _unref(t)('problemList.problem.diagnostic_certainty.placeholder'),
                          required: required,
                          "onUpdate:modelValue": setSelected,
                          onInput: validate
                        }, null, 8, ["error", "label", "model-value", "options", "placeholder", "required", "onUpdate:modelValue", "onInput"])
                      ]),
                      _: 2
                    }, 1032, ["path"]),
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(_unref(DvDateTime), {
                        path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.date_of_onset`)
                      }, {
                        default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, required }) => [
                          _createVNode(_unref(MedKitDateTimeField), {
                            class: "col-span-4 lg:col-span-3",
                            error: fieldErrors[0],
                            label: fieldName,
                            "model-value": value,
                            "date-input-only": "",
                            required: required,
                            "onUpdate:modelValue": setValue,
                            onInput: validate
                          }, null, 8, ["error", "label", "model-value", "required", "onUpdate:modelValue", "onInput"])
                        ]),
                        _: 2
                      }, 1032, ["path"]),
                      _createVNode(_unref(DvDateTime), {
                        path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.date_of_resolution`),
                        "present-if": _unref(getData)(props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.problem_diagnosis_qualifier.active_diagnosis`))?.['|code'] === '73425007'
                      }, {
                        default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, required }) => [
                          _createVNode(_unref(MedKitDateTimeField), {
                            class: "col-span-4 lg:col-span-3",
                            error: fieldErrors[0],
                            label: fieldName,
                            "model-value": value,
                            "date-input-only": "",
                            required: required,
                            "onUpdate:modelValue": setValue,
                            onInput: validate
                          }, null, 8, ["error", "label", "model-value", "required", "onUpdate:modelValue", "onInput"])
                        ]),
                        _: 2
                      }, 1032, ["path", "present-if"])
                    ])
                  ])
                ]),
                _createVNode(_unref(BlockWell), { class: "col-span-12 p-4 md:p-6 space-y-6 lg:space-y-4" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NodeRepeater), {
                      path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.anatomical_location`)
                    }, {
                      default: _withCtx(({ index: anatomicalLocationIndex, remove }) => [
                        _createElementVNode("div", {
                          class: _normalizeClass(["flex space-x-6", {
                      'border-t pt-6 lg:pt-0 lg:border-t-0': anatomicalLocationIndex > 0
                    }])
                        }, [
                          _createElementVNode("div", _hoisted_7, [
                            _createVNode(_unref(DvCodedText), {
                              path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.anatomical_location.${anatomicalLocationIndex}.body_site_name`)
                            }, {
                              default: _withCtx(({ name: fieldName, terminology, value, setValue, errors: fieldErrors, validate, required}) => [
                                _createVNode(_unref(TerminologySearch), { terminology: terminology }, {
                                  default: _withCtx(({ search, searching, results, error: terminologySearchError }) => [
                                    _createVNode(_unref(MedKitSearch), {
                                      class: "w-full lg:w-auto lg:flex-1",
                                      "model-value": value ? { label: value['|value'], value: value } : undefined,
                                      error: terminologySearchError ?? fieldErrors[0],
                                      label: fieldName,
                                      "label-hidden": display.lgAndUp ? anatomicalLocationIndex > 0 : false,
                                      "is-loading": searching,
                                      options: results.map(result => ({
                              label: result['|value'],
                              value: result,
                            })),
                                      placeholder: _unref(t)('problemList.problem.anatomical_location.body_site_name.placeholder'),
                                      required: required,
                                      "onUpdate:query": search,
                                      "onUpdate:modelValue": ($event: any) => (setValue($event ? $event.value : undefined)),
                                      onInput: validate
                                    }, null, 8, ["model-value", "error", "label", "label-hidden", "is-loading", "options", "placeholder", "required", "onUpdate:query", "onUpdate:modelValue", "onInput"])
                                  ]),
                                  _: 2
                                }, 1032, ["terminology"])
                              ]),
                              _: 2
                            }, 1032, ["path"]),
                            _createVNode(_unref(DvCodedText), {
                              path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.anatomical_location.${anatomicalLocationIndex}.laterality`)
                            }, {
                              default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                _createVNode(_unref(MedKitRadioGroup), {
                                  name: `problem_diagnosis.${problemDiagnosisIndex}.anatomical_location.${anatomicalLocationIndex}.laterality`,
                                  class: "flex-shrink-0 mt-4 lg:mt-0 lg:ml-6",
                                  error: fieldErrors[0],
                                  label: fieldName,
                                  "label-hidden": display.lgAndUp ? anatomicalLocationIndex > 0 : false,
                                  "model-value": selected,
                                  options: options,
                                  contained: "",
                                  required: required,
                                  "onUpdate:modelValue": setSelected,
                                  onInput: validate
                                }, null, 8, ["name", "error", "label", "label-hidden", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                              ]),
                              _: 2
                            }, 1032, ["path"]),
                            _createVNode(_unref(DvCodedTextList), {
                              path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.anatomical_location.${anatomicalLocationIndex}.aspect`)
                            }, {
                              default: _withCtx(({ name: fieldName, values, setValues, items, errors: fieldErrors, validate, required }) => [
                                _createVNode(_unref(DvCodedTextFilter), { items: items }, {
                                  default: _withCtx(({ filter, results }) => [
                                    _createVNode(_unref(MedKitSearch), {
                                      class: "flex-1 md:ml-6 mt-4 lg:mt-0",
                                      multiple: "",
                                      "model-value": values.map(value => ({
                              label: value['|value'],
                              value: value,
                            })),
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      "label-hidden": display.lgAndUp ? anatomicalLocationIndex > 0 : false,
                                      "min-search-length": 1,
                                      options: results.map(result => ({
                              label: result['|value'],
                              value: result
                            })),
                                      placeholder: _unref(t)('problemList.problem.anatomical_location.aspect.placeholder'),
                                      required: required,
                                      "onUpdate:query": filter,
                                      "onUpdate:modelValue": ($event: any) => (setValues($event.map(value => value.value))),
                                      onInput: validate
                                    }, null, 8, ["model-value", "error", "label", "label-hidden", "options", "placeholder", "required", "onUpdate:query", "onUpdate:modelValue", "onInput"])
                                  ]),
                                  _: 2
                                }, 1032, ["items"])
                              ]),
                              _: 2
                            }, 1032, ["path"])
                          ]),
                          _createVNode(_unref(MedKitButton), {
                            class: _normalizeClass(["flex-shrink-0", {
                        'mt-2': anatomicalLocationIndex > 0,
                        'mt-8': anatomicalLocationIndex === 0
                      }]),
                            label: _unref(t)('problemList.problem.anatomical_location.remove'),
                            "icon-position": "only",
                            icon: "delete",
                            onClick: remove
                          }, null, 8, ["class", "label", "onClick"])
                        ], 2)
                      ]),
                      footer: _withCtx(({ addItem }) => [
                        _createVNode(_unref(MedKitButton), {
                          class: "w-full",
                          block: "",
                          kind: "repeater",
                          size: "medium",
                          label: _unref(t)('problemList.problem.anatomical_location.add'),
                          icon: "plus",
                          "icon-position": "start",
                          onClick: addItem
                        }, null, 8, ["label", "onClick"])
                      ]),
                      _: 2
                    }, 1032, ["path"])
                  ]),
                  _: 2
                }, 1024),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_unref(DvText), {
                    path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.clinical_description`)
                  }, {
                    default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required }) => [
                      _createVNode(_unref(MedKitTextField), {
                        class: "lg:flex-1 w-full",
                        error: fieldErrors[0],
                        label: fieldName,
                        "model-value": value,
                        multiline: "",
                        placeholder: _unref(t)('problemList.problem.clinical_description.placeholder'),
                        required: required,
                        "onUpdate:modelValue": setValue,
                        onInput: validateIfAlreadyValidated,
                        onBlur: validate
                      }, null, 8, ["error", "label", "model-value", "placeholder", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                    ]),
                    _: 2
                  }, 1032, ["path"]),
                  _hoisted_9,
                  _createElementVNode("div", _hoisted_10, [
                    _createVNode(_unref(DvCodedText), {
                      path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.problem_diagnosis_qualifier.acuity`)
                    }, {
                      default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                        _createVNode(_unref(MedKitSelect), {
                          class: "col-span-2 md:col-span-1",
                          error: fieldErrors[0],
                          label: fieldName,
                          "model-value": selected,
                          options: options,
                          placeholder: _unref(t)('problemList.problem.problem_diagnosis_qualifier.acuity.placeholder'),
                          required: required,
                          "onUpdate:modelValue": setSelected,
                          onInput: validate
                        }, null, 8, ["error", "label", "model-value", "options", "placeholder", "required", "onUpdate:modelValue", "onInput"])
                      ]),
                      _: 2
                    }, 1032, ["path"]),
                    _createVNode(_unref(DvCodedText), {
                      path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.problem_diagnosis_qualifier.progression`)
                    }, {
                      default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                        _createVNode(_unref(MedKitSelect), {
                          class: "col-span-2 md:col-span-1",
                          error: fieldErrors[0],
                          label: fieldName,
                          "model-value": selected,
                          options: options,
                          placeholder: _unref(t)('problemList.problem.problem_diagnosis_qualifier.progression.placeholder'),
                          required: required,
                          "onUpdate:modelValue": setSelected,
                          onInput: validate
                        }, null, 8, ["error", "label", "model-value", "options", "placeholder", "required", "onUpdate:modelValue", "onInput"])
                      ]),
                      _: 2
                    }, 1032, ["path"]),
                    _createVNode(_unref(DvCodedText), {
                      path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.severity`)
                    }, {
                      default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                        _createVNode(_unref(MedKitSelect), {
                          class: "col-span-2 md:col-span-1",
                          error: fieldErrors[0],
                          label: fieldName,
                          "model-value": selected,
                          options: options,
                          placeholder: _unref(t)('problemList.problem.severity.placeholder'),
                          required: required,
                          "onUpdate:modelValue": setSelected,
                          onInput: validate
                        }, null, 8, ["error", "label", "model-value", "options", "placeholder", "required", "onUpdate:modelValue", "onInput"])
                      ]),
                      _: 2
                    }, 1032, ["path"]),
                    _createVNode(_unref(DvCodedText), {
                      path: props.path.add(`problem_diagnosis.${problemDiagnosisIndex}.problem_diagnosis_qualifier.remission_status`)
                    }, {
                      default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                        _createVNode(_unref(MedKitSelect), {
                          class: "col-span-2 md:col-span-1",
                          error: fieldErrors[0],
                          label: fieldName,
                          "model-value": selected,
                          options: options,
                          placeholder: _unref(t)('problemList.problem.problem_diagnosis_qualifier.remission_status.placeholder'),
                          required: required,
                          "onUpdate:modelValue": setSelected,
                          onInput: validate
                        }, null, 8, ["error", "label", "model-value", "options", "placeholder", "required", "onUpdate:modelValue", "onInput"])
                      ]),
                      _: 2
                    }, 1032, ["path"])
                  ])
                ])
              ])
            ]),
            _: 2
          }, 1024)
        ]),
        _: 2
      }, 1032, ["path", "present-if"])
    ]),
    _: 1
  }))
}
}

})